import { type LucideIcon, Settings, BarChart2, Twitter, Calendar } from "lucide-react";

interface Job {
  href: string;
  name: string;
  dates: string;
  title: string;
  icon?: string;
  img?: { light: string; dark: string };
  lucide?: LucideIcon;
}

const jobs: Job[] = [
  {
    href: "https://wisetech.llc",
    name: "Wise Technologies",
    dates: "Mar '26 — Present",
    title: "Founder",
    lucide: Settings,
  },
  {
    href: "https://stablesea.com",
    name: "Stable Sea",
    dates: "Jan '25 — Present",
    title: "Co-Founder",
    img: { light: "/ss-favicon-light.png", dark: "/ss-favicon-dark.png" },
  },
  {
    href: "https://bitment.co",
    name: "Bitment",
    dates: "Jan '25 — Present",
    title: "Co-Founder",
    icon: "bitcoin",
  },
  {
    href: "https://www.tbd.website/",
    name: "Block ($XYZ)",
    dates: "Feb '23 — Jan '25",
    title: "Software Engineer",
    icon: "web",
  },
  {
    href: "https://coinmetrics.io/",
    name: "Coin Metrics",
    dates: "Nov '22 — Feb '23",
    title: "Frontend Team Lead",
    lucide: BarChart2,
  },
  {
    href: "https://coinmetrics.io/",
    name: "Coin Metrics",
    dates: "Aug '21 — Nov '22",
    title: "Senior Frontend Engineer",
    lucide: BarChart2,
  },
  {
    href: "https://www.getsymba.com/",
    name: "Symba",
    dates: "Apr '21 — Aug '21",
    title: "Co-Founder & CTO",
    icon: "lion",
  },
  {
    href: "https://www.kfc.com/",
    name: "KFC Digital",
    dates: "Jan '21 — Apr '21",
    title: "Solutions Architect",
    icon: "chicken",
  },
  {
    href: "https://www.kfc.com/",
    name: "KFC Digital",
    dates: "Feb '20 — Jan '21",
    title: "Senior Software Engineer",
    icon: "chicken",
  },
  {
    href: "https://www.kfc.com/",
    name: "KFC Digital",
    dates: "Oct '18 — Feb '20",
    title: "Applications Developer",
    icon: "chicken",
  },
  {
    href: "https://www.kfc.com/",
    name: "KFC Digital",
    dates: "Oct '17 — Oct '18",
    title: "Software Engineer",
    icon: "chicken",
  },
  {
    href: "https://eltoro.com/",
    name: "El Toro",
    dates: "May '17 — Oct '17",
    title: "Junior Software Engineer",
    icon: "bull",
  },
  {
    href: "https://www.engr.uky.edu/research-faculty/departments/computer-science",
    name: "Univ. of Kentucky",
    dates: "May '16 — May '17",
    title: "Research Assistant (ML)",
    icon: "cat",
  },
  {
    href: "https://www.engr.uky.edu/research-faculty/departments/computer-science",
    name: "Univ. of Kentucky",
    dates: "Aug '13 — May '17",
    title: "BS in Computer Science",
    icon: "cap",
  },
  {
    href: "https://www.google.com/search?q=macauthority",
    name: "MacAuthority",
    dates: "July '12 — Aug '13",
    title: "Apple Technician",
    icon: "apple",
  },
];

const CALENDAR_URL =
  "https://calendar.google.com/appointments/schedules/AcZssZ23Xe-CitTRThETWVb-a3qq0tTerhLls2NtO0FMvN2CF7ZNqOpVFGhsPjpFUebCBdsrlsxS3urS";

export default function Home() {
  return (
    <main className="mx-8 mt-8 max-w-2xl pb-64">
      <header className="mb-8">
        <div className="mb-2">
          <span className="font-h1">Kendall Weihe</span>
          <span className="font-body-md text-muted-foreground ml-3">("why")</span>
        </div>

        <p className="font-body-md text-muted-foreground mb-1">
          Software engineer by trade.
          <br />
          <strong>Thinker</strong> by choice.
        </p>

        <p className="font-body-md text-muted-foreground mb-4">
          Currently operating as a{" "}
          <a
            href="https://wisetech.llc"
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary hover:text-foreground transition-colors"
          >
            Software General Contractor
          </a>
          .
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <a
            href="https://wisetech.llc"
            target="_blank"
            rel="noreferrer noopener"
            className="font-body-sm text-primary hover:text-foreground transition-colors"
          >
            Currently accepting a limited number of new engagements →
          </a>
          <span className="text-muted-foreground font-body-sm">·</span>
          <a
            href={CALENDAR_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="font-body-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <Calendar className="size-3.5" strokeWidth={1.5} />
            Meet with me
          </a>
          <span className="text-muted-foreground font-body-sm">·</span>
          <a
            href="https://x.com/kendallweihe"
            target="_blank"
            rel="noreferrer noopener"
            className="font-body-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <Twitter className="size-3.5" strokeWidth={1.5} />
            @kendallweihe
          </a>
        </div>

        <img src="/kendall.jpg" alt="Kendall Weihe" className="h-32 block mb-4" />

        <em className="font-body-md text-muted-foreground">
          "The mind is not a vessel to be filled but a fire to be kindled." — Plutarch
        </em>
      </header>

      <section>
        <div className="font-h1 mb-4">Curriculum Vitae</div>
        <p className="font-body-md mb-8">
          Software engineering <span className="font-light">generalist</span>
        </p>

        <div className="flex flex-col gap-6">
          {jobs.map((job, i) => (
            <div key={i} className="flex items-start sm:items-center gap-3">
              {job.img ? (
                <>
                  <img src={job.img.light} alt="" className="size-8 flex-none dark:hidden" />
                  <img src={job.img.dark} alt="" className="size-8 flex-none hidden dark:block" />
                </>
              ) : job.lucide ? (
                <job.lucide className="size-8 flex-none" strokeWidth={1.5} />
              ) : (
                <svg className="size-8 flex-none fill-foreground">
                  <use href={`#${job.icon}`} />
                </svg>
              )}
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 min-w-0">
                <a
                  href={job.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-body-md text-primary hover:text-foreground transition-colors sm:min-w-[11rem]"
                >
                  {job.name}
                </a>
                <span className="font-body-sm text-muted-foreground whitespace-nowrap">{job.dates}</span>
                <span className="font-body-sm font-light text-muted-foreground">{job.title}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
